"use client";

import React, { useRef, useMemo, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial } from "@react-three/drei";
import { EffectComposer, Bloom } from "@react-three/postprocessing";
import * as THREE from "three";
import { useTheme } from "next-themes";

const OCTANT_COLORS = [
  "#FF006E",
  "#00D9FF",
  "#FFBE0B",
  "#8338EC",
  "#3A86FF",
  "#FB5607",
  "#06FFA5",
  "#FFFFB3",
];

const getOctantIndex = (x: number, y: number, z: number): number => {
  return (x >= 0 ? 4 : 0) | (y >= 0 ? 2 : 0) | (z >= 0 ? 1 : 0);
};

// Generiert Punkte und die feinen Verbindungslinien
const generateEmbeddingData = (count: number, radius: number) => {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);

  const linePositions = [];
  const lineColors = [];

  const pointsData = [];

  for (let i = 0; i < count; i++) {
    const r = radius * Math.cbrt(Math.random());
    const theta = Math.random() * 2 * Math.PI;
    const phi = Math.acos(2 * Math.random() - 1);

    const x = r * Math.sin(phi) * Math.cos(theta);
    const y = r * Math.sin(phi) * Math.sin(theta);
    const z = r * Math.cos(phi);

    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;

    const octantIdx = getOctantIndex(x, y, z);
    const colorHex = OCTANT_COLORS[octantIdx];
    const color = new THREE.Color(colorHex);

    colors[i * 3] = color.r;
    colors[i * 3 + 1] = color.g;
    colors[i * 3 + 2] = color.b;

    pointsData.push({ x, y, z, octantIdx, color });
  }

  for (let i = 0; i < count; i++) {
    for (let j = i + 1; j < count; j++) {
      const p1 = pointsData[i];
      const p2 = pointsData[j];

      if (p1.octantIdx === p2.octantIdx) {
        const dist = Math.sqrt(
          (p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2 + (p1.z - p2.z) ** 2,
        );
        if (dist < 0.4) {
          linePositions.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
          lineColors.push(
            p1.color.r,
            p1.color.g,
            p1.color.b,
            p2.color.r,
            p2.color.g,
            p2.color.b,
          );
        }
      }
    }
  }

  return {
    positions,
    colors,
    linePositions: new Float32Array(linePositions),
    lineColors: new Float32Array(lineColors),
  };
};

const ParticleCloud = ({
  isHovered,
  isLight,
}: {
  isHovered: boolean;
  isLight: boolean;
}) => {
  const groupRef = useRef<THREE.Group>(null);

  const { positions, colors, linePositions, lineColors } = useMemo(
    () => generateEmbeddingData(450, 1.5),
    [],
  );

  useFrame((state, delta) => {
    if (groupRef.current) {
      const speed = 0.02;
      groupRef.current.rotation.x -= delta * speed;
      groupRef.current.rotation.y -= delta * (speed * 1.5);

      const breathe = Math.sin(state.clock.elapsedTime * 0.8) * 0.02;
      const targetScale = isHovered ? 1.03 : 1.0;
      const finalScale = targetScale + breathe;

      groupRef.current.scale.lerp(
        new THREE.Vector3(finalScale, finalScale, finalScale),
        0.05,
      );
    }
  });

  return (
    <group ref={groupRef} rotation={[0, 0, Math.PI / 4]}>
      {/* Punkte mit Additive oder Normal Blending */}
      <Points
        positions={positions}
        colors={colors}
        stride={3}
        frustumCulled={false}
      >
        <PointMaterial
          transparent={!isLight}
          depthWrite={isLight ? true : false}
          opacity={isLight ? 1.0 : 0.8}
          size={isLight ? 0.08 : 0.07}
          sizeAttenuation={true}
          vertexColors
          blending={isLight ? THREE.NormalBlending : THREE.AdditiveBlending}
          onBeforeCompile={(shader) => {
            shader.fragmentShader = shader.fragmentShader.replace(
              "#include <clipping_planes_fragment>",
              `
              #include <clipping_planes_fragment>
              if (length(gl_PointCoord - vec2(0.5, 0.5)) > 0.5) discard;
              `,
            );
          }}
        />
      </Points>

      {/* Linien */}
      <lineSegments>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            args={[linePositions, 3]}
          />
          <bufferAttribute attach="attributes-color" args={[lineColors, 3]} />
        </bufferGeometry>
        <lineBasicMaterial
          vertexColors
          transparent={!isLight}
          opacity={isLight ? 1.0 : 0.2}
          depthWrite={isLight ? true : false}
          blending={isLight ? THREE.NormalBlending : THREE.AdditiveBlending}
        />
      </lineSegments>
    </group>
  );
};

export default function EmbeddingThumbnail() {
  const [isHovered, setIsHovered] = useState(false);
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: "100%",
        height: "100%",
        borderRadius: "16px",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <Canvas camera={{ position: [0, 0, 1.5] }}>
        <ParticleCloud isHovered={isHovered} isLight={isLight} />

        <EffectComposer enableNormalPass={true}>
          <Bloom
            luminanceThreshold={isLight ? 1.0 : 0.1} // In lightmode bloom fast deaktivieren
            mipmapBlur
            intensity={isLight ? 0 : 1.2}
          />
        </EffectComposer>
      </Canvas>
    </div>
  );
}
